import { useState } from "react";
import {
  Typography,
  Avatar,
  Button,
  Chip,
  Skeleton,
  Box,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  FaChalkboardTeacher,
  FaUserShield,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
  FaPhone,
  FaVenusMars,
  FaFilePdf,
  FaFileCsv,
  FaEnvelope,
} from "react-icons/fa";
import { MdOpenInNew, MdBadge } from "react-icons/md";
import { HiUserGroup } from "react-icons/hi2";
import ImagePreviewModal from "./modal/imagePreview";
import { motion, AnimatePresence } from "framer-motion";

/* ─── Design Tokens ─────────────────────────────────────────── */
const T = {
  blue: "#1565C0",
  blueMid: "#1E88E5",
  blueGhost: "rgba(21,101,192,0.07)",
  blueGhostHover: "rgba(21,101,192,0.13)",
  teal: "#00796B",
  tealGhost: "rgba(0,121,107,0.07)",
  amber: "#F57C00",
  rose: "#C62828",
  slate900: "#0D1B2A",
  slate700: "#334155",
  slate600: "#475569",
  slate500: "#64748B",
  slate400: "#94A3B8",
  slate300: "#CBD5E1",
  slate200: "#E2E8F0",
  slate100: "#F1F5F9",
  slate50: "#F8FAFC",
  white: "#FFFFFF",
  grad: "linear-gradient(135deg, #1565C0 0%, #1E88E5 100%)",
  shadow: "0 1px 3px rgba(13,27,42,0.06), 0 4px 12px rgba(13,27,42,0.03)",
};

const ITEMS_PER_PAGE = 10;

/* ─── Avatar Color Palettes ─────────────────────────────────── */
const PALETTES = [
  ["#1565C0", "#42A5F5"],
  ["#3949AB", "#7986CB"],
  ["#00796B", "#4DB6AC"],
  ["#AD1457", "#F48FB1"],
  ["#6A1B9A", "#CE93D8"],
  ["#F57C00", "#FFB74D"],
  ["#558B2F", "#AED581"],
  ["#1565C0", "#80CBC4"],
];

/* ─── Column Header ─────────────────────────────────────────── */
const TableHeader = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: 2.5,
      py: 1.2,
      background: `linear-gradient(135deg, rgba(21,101,192,0.05) 0%, rgba(30,136,229,0.02) 100%)`,
      borderBottom: `1px solid ${T.slate100}`,
    }}
  >
    <Box sx={{ flex: "0 0 44px" }} />
    <HeaderCell label="Teacher" flex="2 1 0" />
    <HeaderCell label="Email" flex="2 1 0" />
    <HeaderCell label="Contact" flex="1.5 1 0" hide="sm" />
    <HeaderCell label="Gender" flex="0 0 80px" hide="md" />
    <HeaderCell label="Code" flex="0 0 90px" />
    <HeaderCell label="Role" flex="0 0 120px" />
    <Box sx={{ flex: "0 0 100px" }} />
  </Box>
);

const HeaderCell = ({ label, flex, hide }) => (
  <Typography
    sx={{
      flex,
      fontSize: "0.6rem",
      fontWeight: 800,
      color: T.slate400,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      display: {
        xs: hide === "sm" ? "none" : hide === "md" ? "none" : "block",
        sm: hide === "sm" ? "none" : "block",
        md: "block",
      },
    }}
  >
    {label}
  </Typography>
);

/* ─── Teacher Row ────────────────────────────────────────────── */
const TeacherRow = ({ teacher, onEdit, colorIndex, isLast, onAvatarClick }) => {
  const initials =
    teacher.name
      ?.split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "T";
  const [c1, c2] = PALETTES[colorIndex % PALETTES.length];

  const genderStyle =
    teacher.gender === "Male"
      ? {
          bg: "rgba(21,101,192,0.07)",
          color: T.blue,
          border: "rgba(21,101,192,0.2)",
        }
      : teacher.gender === "Female"
      ? {
          bg: "rgba(173,20,87,0.07)",
          color: "#AD1457",
          border: "rgba(173,20,87,0.2)",
        }
      : { bg: T.slate100, color: T.slate400, border: T.slate200 };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2.5,
          py: 1.2,
          minHeight: 58,
          borderBottom: isLast ? "none" : `1px solid rgba(226,232,240,0.5)`,
          background: T.white,
          transition: "all 0.15s ease",
          "&:hover": {
            background: `linear-gradient(90deg, rgba(21,101,192,0.025) 0%, rgba(255,255,255,0) 60%)`,
          },
          "&:hover .row-actions": { opacity: 1, transform: "translateX(0)" },
        }}
      >
        {/* Avatar */}
        <Box sx={{ flex: "0 0 44px" }}>
          <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }}>
            <Avatar
              src={teacher.passport || undefined}
              onClick={() =>
                onAvatarClick({
                  src: teacher.passport,
                  name: teacher.name,
                  fallback: initials,
                  avatarColor: `linear-gradient(135deg, ${c1}, ${c2})`,
                })
              }
              sx={{
                width: 34,
                height: 34,
                fontSize: "0.7rem",
                fontWeight: 800,
                background: `linear-gradient(135deg, ${c1}, ${c2})`,
                boxShadow: `0 2px 8px ${c1}45`,
                border: "2px solid #fff",
                cursor: "pointer",
              }}
            >
              {!teacher.passport && initials}
            </Avatar>
          </motion.div>
        </Box>

        {/* Name */}
        <Box sx={{ flex: "2 1 0", minWidth: 0, pr: 2 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.85rem",
              color: T.slate900,
              textTransform: "capitalize",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.35,
            }}
          >
            {teacher.name}
          </Typography>
          <Typography
            sx={{ fontSize: "0.68rem", color: T.slate400, fontWeight: 500 }}
          >
            {teacher.is_class_admin ? "Class Admin" : "Teacher"}
          </Typography>
        </Box>

        {/* Email */}
        <Box sx={{ flex: "2 1 0", minWidth: 0, pr: 2 }}>
          {teacher.email ? (
            <Stack direction="row" alignItems="center" spacing={0.6}>
              <FaEnvelope size={10} color={T.slate400} />
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: T.slate600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {teacher.email}
              </Typography>
            </Stack>
          ) : (
            <Typography sx={{ fontSize: "0.7rem", color: T.slate300 }}>
              —
            </Typography>
          )}
        </Box>

        {/* Phone */}
        <Box
          sx={{
            flex: "1.5 1 0",
            minWidth: 0,
            pr: 2,
            display: { xs: "none", sm: "block" },
          }}
        >
          {teacher.phone ? (
            <Stack direction="row" alignItems="center" spacing={0.6}>
              <FaPhone size={10} color={T.slate400} />
              <Typography sx={{ fontSize: "0.78rem", color: T.slate600 }}>
                {teacher.phone}
              </Typography>
            </Stack>
          ) : (
            <Typography sx={{ fontSize: "0.7rem", color: T.slate300 }}>
              —
            </Typography>
          )}
        </Box>

        {/* Gender */}
        <Box sx={{ flex: "0 0 80px", display: { xs: "none", md: "block" } }}>
          {teacher.gender ? (
            <Chip
              icon={<FaVenusMars size={8} />}
              label={teacher.gender}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.62rem",
                fontWeight: 700,
                background: genderStyle.bg,
                color: genderStyle.color,
                border: `1px solid ${genderStyle.border}`,
                "& .MuiChip-label": { px: 0.7 },
                "& .MuiChip-icon": {
                  color: genderStyle.color,
                  ml: 0.5,
                  fontSize: "8px",
                },
              }}
            />
          ) : (
            <Typography sx={{ fontSize: "0.7rem", color: T.slate300 }}>
              —
            </Typography>
          )}
        </Box>

        {/* Code */}
        <Box sx={{ flex: "0 0 90px" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.2,
              py: 0.3,
              borderRadius: 1.5,
              background: T.blueGhost,
              border: `1px solid rgba(21,101,192,0.16)`,
            }}
          >
            <MdBadge size={10} color={T.blue} />
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 800,
                color: T.blue,
                letterSpacing: "0.06em",
                fontFamily: "'JetBrains Mono','Fira Code',monospace",
              }}
            >
              {teacher.code || "—"}
            </Typography>
          </Box>
        </Box>

        {/* Role */}
        <Box sx={{ flex: "0 0 120px" }}>
          {teacher.is_class_admin ? (
            <Chip
              icon={<FaUserShield size={8} />}
              label="Class Admin"
              size="small"
              sx={{
                height: 20,
                fontSize: "0.62rem",
                fontWeight: 700,
                background: T.tealGhost,
                color: T.teal,
                border: "1px solid rgba(0,121,107,0.22)",
                "& .MuiChip-label": { px: 0.8 },
                "& .MuiChip-icon": { color: T.teal, ml: 0.5, fontSize: "8px" },
              }}
            />
          ) : (
            <Chip
              icon={<FaChalkboardTeacher size={8} />}
              label="Teacher"
              size="small"
              sx={{
                height: 20,
                fontSize: "0.62rem",
                fontWeight: 700,
                background: T.slate100,
                color: T.slate500,
                border: `1px solid ${T.slate200}`,
                "& .MuiChip-label": { px: 0.8 },
                "& .MuiChip-icon": {
                  color: T.slate400,
                  ml: 0.5,
                  fontSize: "8px",
                },
              }}
            />
          )}
        </Box>

        {/* Actions */}
        <Box
          className="row-actions"
          sx={{
            flex: "0 0 100px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.7,
            opacity: 0.3,
            transform: "translateX(5px)",
            transition: "all 0.18s ease",
          }}
        >
          <Tooltip title="View details" placement="top" arrow>
            <IconButton
              size="small"
              onClick={() => onEdit(teacher)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.5,
                border: `1px solid ${T.slate200}`,
                color: T.slate500,
                "&:hover": {
                  color: T.blue,
                  borderColor: `${T.blue}40`,
                  background: T.blueGhost,
                },
              }}
            >
              <FaEye size={10} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit teacher" placement="top" arrow>
            <Button
              size="small"
              variant="contained"
              endIcon={<MdOpenInNew size={9} />}
              onClick={() => onEdit(teacher)}
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.68rem",
                px: 1.2,
                py: 0,
                height: 28,
                minWidth: 0,
                background: T.grad,
                boxShadow: "0 2px 8px rgba(21,101,192,0.25)",
                "&:hover": {
                  background: "linear-gradient(135deg, #0D47A1, #1565C0)",
                  boxShadow: "0 3px 12px rgba(21,101,192,0.35)",
                },
              }}
            >
              Edit
            </Button>
          </Tooltip>
        </Box>
      </Box>
    </motion.div>
  );
};

/* ─── Skeleton Row ───────────────────────────────────────────── */
const SkeletonRow = ({ isLast }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: 2.5,
      py: 1.2,
      minHeight: 58,
      borderBottom: isLast ? "none" : `1px solid rgba(226,232,240,0.5)`,
      background: T.white,
    }}
  >
    <Box sx={{ flex: "0 0 44px" }}>
      <Skeleton variant="circular" width={34} height={34} />
    </Box>
    <Box sx={{ flex: "2 1 0", pr: 2 }}>
      <Skeleton width="50%" height={13} />
      <Skeleton width="30%" height={10} sx={{ mt: 0.4 }} />
    </Box>
    <Box sx={{ flex: "2 1 0", pr: 2 }}>
      <Skeleton width="65%" height={13} />
    </Box>
    <Box sx={{ flex: "1.5 1 0", pr: 2, display: { xs: "none", sm: "block" } }}>
      <Skeleton width="55%" height={13} />
    </Box>
    <Box sx={{ flex: "0 0 80px", display: { xs: "none", md: "block" } }}>
      <Skeleton width={52} height={20} sx={{ borderRadius: 99 }} />
    </Box>
    <Box sx={{ flex: "0 0 90px" }}>
      <Skeleton width={52} height={22} sx={{ borderRadius: 1.5 }} />
    </Box>
    <Box sx={{ flex: "0 0 120px" }}>
      <Skeleton width={70} height={20} sx={{ borderRadius: 99 }} />
    </Box>
    <Box
      sx={{
        flex: "0 0 100px",
        display: "flex",
        justifyContent: "flex-end",
        gap: 0.7,
      }}
    >
      <Skeleton width={28} height={28} sx={{ borderRadius: 1.5 }} />
      <Skeleton width={48} height={28} sx={{ borderRadius: 1.5 }} />
    </Box>
  </Box>
);

/* ─── Pagination Bar ─────────────────────────────────────────── */
const PaginationBar = ({ page, totalPages, total, onPrev, onNext, onPage }) => {
  const start = (page - 1) * ITEMS_PER_PAGE + 1;
  const end = Math.min(page * ITEMS_PER_PAGE, total);

  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1))
      pages.push(i);
    else if (pages[pages.length - 1] !== "…") pages.push("…");
  }

  const navSx = (disabled) => ({
    width: 28,
    height: 28,
    borderRadius: 1.5,
    border: `1px solid ${T.slate200}`,
    color: disabled ? T.slate300 : T.slate500,
    "&:hover:not(:disabled)": {
      borderColor: T.blue,
      color: T.blue,
      background: T.blueGhost,
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2.5,
        py: 1.5,
        borderTop: `1px solid ${T.slate100}`,
        background: T.slate50,
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Typography
        sx={{ fontSize: "0.7rem", color: T.slate400, fontWeight: 500 }}
      >
        Showing{" "}
        <Box component="span" sx={{ fontWeight: 800, color: T.slate700 }}>
          {start}–{end}
        </Box>{" "}
        of{" "}
        <Box component="span" sx={{ fontWeight: 800, color: T.slate700 }}>
          {total}
        </Box>{" "}
        teachers
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <IconButton
          size="small"
          onClick={onPrev}
          disabled={page === 1}
          sx={navSx(page === 1)}
        >
          <FaChevronLeft size={9} />
        </IconButton>
        {pages.map((p, i) =>
          p === "…" ? (
            <Typography
              key={`el-${i}`}
              sx={{ fontSize: "0.7rem", color: T.slate400, px: 0.3 }}
            >
              …
            </Typography>
          ) : (
            <Box
              key={p}
              component={motion.div}
              whileTap={{ scale: 0.92 }}
              onClick={() => onPage(p)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: "0.7rem",
                fontWeight: p === page ? 800 : 600,
                color: p === page ? T.white : T.slate500,
                background: p === page ? T.grad : "transparent",
                border: p === page ? "none" : `1px solid ${T.slate200}`,
                boxShadow:
                  p === page ? "0 2px 8px rgba(21,101,192,0.3)" : "none",
                transition: "all 0.13s ease",
                "&:hover":
                  p !== page
                    ? {
                        borderColor: T.blue,
                        color: T.blue,
                        background: T.blueGhost,
                      }
                    : {},
              }}
            >
              {p}
            </Box>
          )
        )}
        <IconButton
          size="small"
          onClick={onNext}
          disabled={page === totalPages}
          sx={navSx(page === totalPages)}
        >
          <FaChevronRight size={9} />
        </IconButton>
      </Stack>
    </Box>
  );
};

/* ─── Export Bar ─────────────────────────────────────────────── */
const ExportBar = ({ onPDF, onCSV }) => (
  <Box
    sx={{
      px: 2.5,
      py: 2,
      borderTop: `1px solid ${T.slate100}`,
      display: "flex",
      gap: 1.5,
      flexWrap: "wrap",
      background: T.white,
    }}
  >
    <Typography
      sx={{
        fontSize: "0.65rem",
        fontWeight: 800,
        color: T.slate400,
        textTransform: "uppercase",
        letterSpacing: "0.8px",
        display: "flex",
        alignItems: "center",
        mr: "auto",
      }}
    >
      Export Staff Data
    </Typography>
    <Button
      variant="outlined"
      size="small"
      startIcon={<FaFilePdf size={11} />}
      onClick={onPDF}
      sx={{
        borderRadius: 2,
        textTransform: "none",
        fontWeight: 700,
        fontSize: "0.78rem",
        px: 2,
        py: 0.8,
        borderColor: "rgba(198,40,40,0.3)",
        color: T.rose,
        "&:hover": {
          borderColor: T.rose,
          background: T.roseGhost || "rgba(198,40,40,0.05)",
        },
      }}
    >
      Export PDF
    </Button>
    <Button
      variant="outlined"
      size="small"
      startIcon={<FaFileCsv size={11} />}
      onClick={onCSV}
      sx={{
        borderRadius: 2,
        textTransform: "none",
        fontWeight: 700,
        fontSize: "0.78rem",
        px: 2,
        py: 0.8,
        borderColor: "rgba(0,121,107,0.3)",
        color: T.teal,
        "&:hover": { borderColor: T.teal, background: T.tealGhost },
      }}
    >
      Export CSV
    </Button>
  </Box>
);

/* ─── Empty State ────────────────────────────────────────────── */
const EmptyState = () => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <Box
      sx={{
        border: `1.5px dashed rgba(21,101,192,0.18)`,
        borderRadius: 2.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        gap: 1.5,
        background:
          "linear-gradient(135deg, rgba(21,101,192,0.02), rgba(255,255,255,0))",
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: 3,
          background: T.blueGhost,
          border: `1px dashed rgba(21,101,192,0.25)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <HiUserGroup size={22} color={T.slate400} />
      </Box>
      <Typography
        sx={{ fontSize: "0.9rem", fontWeight: 700, color: T.slate600 }}
      >
        No teachers found
      </Typography>
      <Typography sx={{ fontSize: "0.76rem", color: T.slate400 }}>
        Try adjusting your search or add a new teacher
      </Typography>
    </Box>
  </motion.div>
);

/* ─── Main TeachersGrid ──────────────────────────────────────── */
const TeachersGrid = ({
  loading,
  teachers,
  onEdit,
  handleDownloadTeacherCSV,
  handleDownloadTeacherPDF,
}) => {
  const [page, setPage] = useState(1);
  const [previewData, setPreviewData] = useState(null);

  const safeTeachers = teachers || [];
  const totalPages = Math.max(
    1,
    Math.ceil(safeTeachers.length / ITEMS_PER_PAGE)
  );
  const safePage = Math.min(page, totalPages);
  const paged = safeTeachers.slice(
    (safePage - 1) * ITEMS_PER_PAGE,
    safePage * ITEMS_PER_PAGE
  );

  if (loading) {
    return (
      <Box
        sx={{
          border: `1px solid ${T.slate200}`,
          borderRadius: 2.5,
          overflow: "hidden",
          boxShadow: T.shadow,
        }}
      >
        <TableHeader />
        {Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
          <SkeletonRow key={i} isLast={i === ITEMS_PER_PAGE - 1} />
        ))}
      </Box>
    );
  }

  if (safeTeachers.length === 0) return <EmptyState />;

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${T.slate200}`,
          borderRadius: 2.5,
          overflow: "hidden",
          boxShadow: T.shadow,
        }}
      >
        <TableHeader />
        <AnimatePresence mode="popLayout">
          {paged.map((teacher, i) => (
            <TeacherRow
              key={teacher.id}
              teacher={teacher}
              onEdit={onEdit}
              colorIndex={(safePage - 1) * ITEMS_PER_PAGE + i}
              isLast={i === paged.length - 1 && totalPages === 1}
              onAvatarClick={setPreviewData}
            />
          ))}
        </AnimatePresence>

        {totalPages > 1 && (
          <PaginationBar
            page={safePage}
            totalPages={totalPages}
            total={safeTeachers.length}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            onPage={(p) => setPage(p)}
          />
        )}
        <ExportBar
          onPDF={handleDownloadTeacherPDF}
          onCSV={handleDownloadTeacherCSV}
        />
      </Box>

      <ImagePreviewModal
        open={!!previewData}
        onClose={() => setPreviewData(null)}
        src={previewData?.src}
        name={previewData?.name}
        subtitle={previewData?.subtitle}
        fallback={previewData?.fallback}
        avatarColor={previewData?.avatarColor}
      />
    </>
  );
};

export default TeachersGrid;
